import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  MsalService,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import {
  EventMessage,
  AuthenticationResult,
  InteractionStatus,
  EventType,
  InteractionType,
  PopupRequest,
  PromptValue,
  RedirectRequest,
} from '@azure/msal-browser';
import { filter } from 'rxjs/operators';


import { createClaimsTable } from '../claim-utils';
import { b2cPolicies } from '../app.config';
import { Claim } from '../models/claim';

@Injectable({ providedIn: 'root' })
export class LoginService {
  private claimsSubject = new BehaviorSubject<Claim[]>([]);
  claims$ = this.claimsSubject.asObservable();
  private userIdSubject = new BehaviorSubject<string>('');
  userId$ = this.userIdSubject.asObservable();
  loginDisplay = false;
  isLoggedIn = false;
  displayedColumns: string[] = ['claim', 'value', 'description'];
  userName!: string;
  // userId!: number;
  userId!: string;
  userRoles: string[] = [];

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {

    const account = this.authService.instance.getActiveAccount()
      || this.authService.instance.getAllAccounts()[0];

    if (account) {
      console.log('[LoginService] Restoring session with account:', account);
      this.authService.instance.setActiveAccount(account);
      this.getClaims(account.idTokenClaims);
    }


    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
        const claims = payload.account.idTokenClaims;
        this.getClaims(claims);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        const claims =
          this.authService.instance.getActiveAccount()?.idTokenClaims;
        this.getClaims(claims);
      });


  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    this.isLoggedIn = this.loginDisplay;
  }

  getClaims(claims: any) {

    const account = this.authService.instance.getActiveAccount();
    const userId = account?.localAccountId ?? claims?.sub;

    if (userId) {
      console.log('[LoginService] Using userId:', userId);
      this.userIdSubject.next(userId);
      this.userId = userId;
    } else {
      console.warn('[LoginService] No usable user ID found.');
    }

    // Optional: set userName from claims
    this.userName =
      claims?.['given_name'] + ', ' + claims?.['family_name'];

    // Optional: handle userRoles if present
    if (
      claims != null &&
      'extension_userRoles' in claims &&
      typeof claims.extension_userRoles === 'string'
    ) {
      this.userRoles = claims.extension_userRoles.split(',');
    } else {
      this.userRoles = [];
    }
  }


  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    let signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      prompt: PromptValue.LOGIN, // force user to reauthenticate with their new password
      scopes: [],
    };

    userFlowRequest = signUpSignInFlowRequest;

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      } else {
        this.authService
          .loginPopup(userFlowRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
          ...userFlowRequest,
        } as RedirectRequest);
      } else {
        this.authService.loginRedirect(userFlowRequest);
      }
    }
  }

  logout() {
    const activeAccount =
      this.authService.instance.getActiveAccount() ||
      this.authService.instance.getAllAccounts()[0];

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        account: activeAccount,
      });
    } else {
      this.authService.logoutRedirect({
        account: activeAccount,
      });
    }
  }
}
